import { Fragment, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { SectionLabel, MaskedLineInView, EASE } from "@/components/Reveal";
import { services } from "@/data/site";

// Reserve STICKY_TOP above the card (nav clearance) and BOTTOM_GAP below it
// (breathing room), and cap the card to whatever's left of the viewport so
// it always fits on screen instead of having its bottom cut off.
const STICKY_TOP = 128;
const BOTTOM_GAP = 48;
const CARD_MAX_HEIGHT = `calc(100vh - ${STICKY_TOP + BOTTOM_GAP}px)`;

const ServiceCard = ({ svc, index }) => (
  <div
    data-testid={`service-card-${index}`}
    className="glass-surface !shadow-none rounded-3xl p-4 sm:p-5 pb-7 flex flex-col overflow-hidden"
    style={{ maxHeight: CARD_MAX_HEIGHT }}
  >
    <div className="rounded-2xl overflow-hidden group flex-1 min-h-[140px]">
      <img
        src={svc.image}
        alt={svc.name}
        className="w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-[1.05]"
      />
    </div>
    <div className="px-2 sm:px-4 shrink-0">
      <h3 className="mt-6 text-2xl sm:text-[30px] font-semibold text-[#8C5EBA] tracking-tight">
        {svc.name}
      </h3>
      <p className="mt-3 text-base sm:text-lg leading-relaxed text-[#111]/60 max-w-2xl font-normal line-clamp-2">{svc.desc}</p>
      <Link
        to={`/services/${svc.slug}`}
        className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-[#8C5EBA] hover:underline"
        data-testid={`service-learn-more-${svc.slug}`}
      >
        Learn More <ArrowRight size={14} />
      </Link>
    </div>
    <div className="marquee mt-6 shrink-0" data-testid={`service-chips-marquee-${index}`}>
      <div className="marquee-track" style={{ animationDuration: `${20 + index * 3}s` }}>
        {[...svc.chips, ...svc.chips].map((c, i) => (
          <span key={i} className="chip">{c}</span>
        ))}
      </div>
    </div>
  </div>
);

const RollingItem = ({ name, active, onClick }) => (
  <button
    onClick={onClick}
    aria-label={name}
    className="relative block overflow-hidden text-left group"
  >
    <span className="block whitespace-nowrap text-3xl xl:text-[36px] leading-[1.55] tracking-tight font-semibold opacity-0" aria-hidden="true">
      {name}
    </span>
    <motion.span
      className="absolute inset-0 block whitespace-nowrap text-3xl xl:text-[36px] leading-[1.55] tracking-tight font-medium text-[#111] group-hover:text-[#8C5EBA]/60 transition-colors duration-300"
      animate={{ y: active ? "-102%" : "0%" }}
      transition={{ duration: 0.55, ease: EASE }}
    >
      {name}
    </motion.span>
    <motion.span
      aria-hidden="true"
      className="absolute inset-0 block whitespace-nowrap text-3xl xl:text-[36px] leading-[1.55] tracking-tight font-semibold text-[#8C5EBA]"
      animate={{ y: active ? "0%" : "102%" }}
      transition={{ duration: 0.55, ease: EASE }}
    >
      {name}
    </motion.span>
  </button>
);

// Every card sticks at the same top offset, so once the next card scrolls
// up and reaches that position it fully overlaps the previous one instead
// of leaving a peeking sliver — z-index alone decides who's on top.
const StackingCard = ({ index, registerRef, children }) => (
  <div
    ref={(el) => registerRef(index, el)}
    className="sticky"
    style={{ top: `${STICKY_TOP}px`, zIndex: 10 + index }}
  >
    {children}
  </div>
);

// A plain sibling (not a wrapper around StackingCard) so every sticky card
// still shares the same containing block — wrapping each card individually
// would give it its own small containing block, capping how long it can
// stay stuck to roughly its own height and breaking the overlap entirely.
// As a shared sibling, this just delays when the next card's natural flow
// position reaches the sticky offset, giving a reading pause before it
// starts covering the current one.
const CARD_PAUSE_VH = 45;
const CardPause = ({ registerRef, index }) => (
  <div
    ref={(el) => registerRef(index, el)}
    style={{ height: `${CARD_PAUSE_VH}vh` }}
    aria-hidden="true"
  />
);

const WhatWeDo = () => {
  const [active, setActive] = useState(0);
  const cardRefs = useRef([]);
  // Refs to the non-sticky CardPause spacers between cards. Because pause
  // elements are never sticky, getBoundingClientRect().bottom + scrollY is
  // always their constant natural document position regardless of how far
  // the page has scrolled — the perfect anchor for computing card targets.
  const pauseRefs = useRef([]);
  // Ref to the flex column that contains all cards and pauses. Its top is
  // the accurate anchor for card 0 (the first item has no preceding pause).
  const columnRef = useRef(null);

  const registerCard = (index, el) => { cardRefs.current[index] = el; };
  const registerPause = (index, el) => { pauseRefs.current[index] = el; };

  // Derive the active card directly from current scroll position each tick,
  // instead of reacting to one-off intersection events — a covered card
  // never stops "intersecting" (it's still sitting there, just hidden
  // underneath), so an event-driven check never fires again on the way
  // back up. Re-measuring fresh on every scroll always gets the right
  // answer in both directions: whichever card has reached (or passed) the
  // sticky offset is the one currently on top, since z-index only ever
  // increases with index.
  useEffect(() => {
    const update = () => {
      let next = 0;
      cardRefs.current.forEach((el, i) => {
        if (el && el.getBoundingClientRect().top <= STICKY_TOP + 2) next = i;
      });
      setActive(next);
    };
    update();
    const lenis = window.__lenis;
    if (lenis) {
      lenis.on("scroll", update);
      return () => lenis.off("scroll", update);
    }
    window.addEventListener("scroll", update, { passive: true });
    return () => window.removeEventListener("scroll", update);
  }, []);

  // Scroll to the natural document position of card i.
  //
  // Strategy: use non-sticky elements as position anchors.
  // For any non-sticky element E: E.getBoundingClientRect().top + scrollY
  // equals E's constant natural document top — true at ANY scroll position.
  //
  // Card 0 → use the flex column container's top (container is not sticky).
  // Card i → use pause[i-1].getBoundingClientRect().bottom + scrollY + gap,
  //          which equals card[i]'s natural document top (pause immediately
  //          precedes card[i] with only a flex gap between them).
  //
  // Sticky cards' own getBoundingClientRect is unreliable once stuck — it
  // returns STICKY_TOP regardless of where the card actually lives in the
  // document, making any target computed from it scroll-position-dependent.
  const scrollToCard = (i) => {
    if (!window.__lenis) return;

    let naturalTop;

    if (i === 0) {
      const col = columnRef.current;
      if (!col) return;
      naturalTop = col.getBoundingClientRect().top + window.scrollY;
    } else {
      const pause = pauseRefs.current[i - 1];
      const col = columnRef.current;
      if (!pause || !col) return;
      const gap = parseFloat(getComputedStyle(col).rowGap) || 0;
      naturalTop = pause.getBoundingClientRect().bottom + window.scrollY + gap;
    }

    window.__lenis.scrollTo(Math.max(0, naturalTop - STICKY_TOP));
  };

  return (
    <section id="what-we-do" data-testid="what-we-do-section" className="relative z-10 site-container pt-24 sm:pt-32 pb-16">
      {/* label left / heading pushed right — matches Figma flex-between */}
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-8">
        <SectionLabel data-testid="what-we-do-label">What We Do</SectionLabel>
        <h2 className="text-4xl sm:text-6xl lg:text-[68px] font-normal tracking-tight leading-[1.06] lg:text-right lg:max-w-[820px]">
          <MaskedLineInView>Everything A Brand</MaskedLineInView>
          <MaskedLineInView delay={0.12}>
            <span className="text-[#8C5EBA] font-medium">Needs To Get Seen</span>
          </MaskedLineInView>
        </h2>
      </div>

      <div className="mt-16 sm:mt-24 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="hidden lg:block lg:col-span-4">
          <div className="sticky top-40" data-testid="services-sticky-list">
            {services.map((s, i) => (
              <RollingItem key={s.num} name={s.name} active={active === i} onClick={() => scrollToCard(i)} />
            ))}
            <Link to="/contact" data-testid="talk-to-team-btn" className="btn-pill btn-lav mt-8 !py-3 !px-6 text-[15px] !font-semibold">
              Talk To Our Team
              <ArrowRight size={16} className="btn-arrow" />
            </Link>
          </div>
        </div>

        <div className="lg:col-span-8">
          <div ref={columnRef} className="flex flex-col gap-8 lg:gap-6">
            {services.map((s, i) => (
              <Fragment key={s.num}>
                <StackingCard index={i} registerRef={registerCard}>
                  <ServiceCard svc={s} index={i} />
                </StackingCard>
                {i < services.length - 1 && <CardPause registerRef={registerPause} index={i} />}
              </Fragment>
            ))}
          </div>
          <div className="lg:hidden mt-8">
            <Link to="/contact" className="btn-pill btn-lav !py-3 !px-6 text-[15px] !font-semibold">
              Talk To Our Team
              <ArrowRight size={16} className="btn-arrow" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhatWeDo;
